// EditScheduleModal.js
import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,   
} from "@mui/material";
import { GoogleMap, Marker, InfoWindow, Circle } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import { textStyles } from "../../../../components/CustomText";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";
const mapContainerStyle = { width: "100%", height: "100%" };
const markerIconUrl =
  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png";

const fullDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Keeps 0 in number inputs; only null/undefined become empty. */
const toNumberInputValue = (value) =>
  value === null || value === undefined ? "" : value;

const EditScheduleModal = ({ open, onClose, worker, onUpdateSuccess }) => {
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
const { t, i18n } = useTranslation();

  const getApiErrorMessage = (responseText) => {
    try {
      const data = JSON.parse(responseText);
      const lang = i18n.language?.split("-")[0] || "en";
      if (lang === "es" && data.message_es) return data.message_es;
      if (data.message_en) return data.message_en;
      if (data.message) return data.message;
    } catch {
      // response is not JSON
    }
    return responseText || t("editModal.toast.error");
  };
  const schedule = worker?.__raw?.attendanceSettings?.schedule || {};
  const location = worker?.__raw?.attendanceSettings?.workLocation || {};

  const convertUTCToLocalInputTime = (utcTimeValue) => {
    if (!utcTimeValue || utcTimeValue === "-") return "";

    const localTime = convertUTCToLocalDateTime(utcTimeValue, {
      locale: "en-GB",
      formatOptions: { hour: "2-digit", minute: "2-digit", hour12: false },
    });

    // `type="time"` expects HH:mm
    if (!localTime || localTime === "—") return "";
    return localTime.trim().slice(0, 5);
  };

  const [form, setForm] = useState({
    startTime: convertUTCToLocalInputTime(schedule.startTime),
    endTime: convertUTCToLocalInputTime(schedule.endTime),
    graceMinutes: toNumberInputValue(schedule.graceMinutes),
    breakMinutes: toNumberInputValue(schedule.breakPolicy?.unpaid),
    workingDays: schedule.workingDays || [],
    locationName: location.locationName || "",
    radiusMeters: location.radiusMeters || 100,
  });

  const [currentLocation, setCurrentLocation] = useState(
    location.latitude && location.longitude
      ? [location.latitude, location.longitude]
      : null
  );
  const [loading, setLoading] = useState(false); 
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);
  // Reset form when worker changes
  useEffect(() => {
    if (worker) {
      const newSchedule = worker.__raw?.attendanceSettings?.schedule || {};
      const workLoc = worker.__raw?.attendanceSettings?.workLocation;
      const rawWorkLat = worker.__raw?.work_latitude;
      const rawWorkLng = worker.__raw?.work_longitude;
      const rawLat = worker.__raw?.latitude;
      const rawLng = worker.__raw?.longitude;
      const localStartTime = convertUTCToLocalInputTime(newSchedule.startTime);
      const localEndTime = convertUTCToLocalInputTime(newSchedule.endTime);

      console.log("🕒 [EDIT][UTC->LOCAL] Prefilling edit form from API UTC times", {
        workerId: worker.id,
        utcStartFromApi: newSchedule.startTime || "-",
        utcEndFromApi: newSchedule.endTime || "-",
        localStartForInput: localStartTime || "-",
        localEndForInput: localEndTime || "-",
        timezoneUsed: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      setForm({
        startTime: localStartTime,
        endTime: localEndTime,
        graceMinutes: toNumberInputValue(newSchedule.graceMinutes),
        breakMinutes: toNumberInputValue(newSchedule.breakPolicy?.unpaid),
        workingDays: newSchedule.workingDays || [],
        locationName: workLoc?.locationName || "",
        radiusMeters: workLoc?.radiusMeters || 100,
      });

      if (workLoc?.latitude && workLoc?.longitude) {
        setCurrentLocation([workLoc.latitude, workLoc.longitude]);
      } else if (rawWorkLat && rawWorkLng) {
        setCurrentLocation([parseFloat(rawWorkLat), parseFloat(rawWorkLng)]);
      } else if (rawLat && rawLng) {
        setCurrentLocation([parseFloat(rawLat), parseFloat(rawLng)]);
      }
    }
  }, [worker]);

  // Get current location if none saved
  useEffect(() => {
    if (!currentLocation && navigator.geolocation) {
      getPositionWithFallback(
        (pos) => {
          setCurrentLocation([pos.coords.latitude, pos.coords.longitude]);
        },
        (err) => {
          console.warn("Geolocation error:", err);
        }
      );
    }
  }, [currentLocation]);

  // Pick up the user's location as soon as they allow it in the browser.
  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      if (!currentLocation) {
        getPositionWithFallback(
          (pos) => setCurrentLocation([pos.coords.latitude, pos.coords.longitude]),
          (err) => console.warn("Geolocation error:", err)
        );
      }
    },
  });

  const toggleDay = (day) => {
    setForm((prev) => {
      const updated = prev.workingDays.includes(day)
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      return { ...prev, workingDays: updated };
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const convertLocalTimeToUTCTimeOnly = (timeStr) => {
    if (!timeStr) return "";

    const utcISO = convertLocalDateTimeToUTC(timeStr);
    if (!utcISO) return "";

    const utcTime = new Date(utcISO).toISOString().slice(11, 16);
    console.log(
      "🕒 [EDIT][LOCAL->UTC] Local input time converted for API storage",
      {
        localInput: timeStr,
        utcISO,
        utcTimeOnlyForPayload: utcTime,
      }
    );
    return utcTime;
  };

  const handleSubmit = async () => {
    if (!worker) return;

    if (form.startTime && form.endTime && form.endTime <= form.startTime) {
      toast.error(t("editModal.validation.shiftEndAfterStart"));
      return;
    }

    const payload = {
      schedule: {
        days: form.workingDays,
        start: convertLocalTimeToUTCTimeOnly(form.startTime),
        end: convertLocalTimeToUTCTimeOnly(form.endTime),
        breakPolicy: { unpaid: Number(form.breakMinutes) || 0 },
        graceMinutes: Number(form.graceMinutes) || 0,
      },
      location: {
        lat: currentLocation ? currentLocation[0] : null,
        lng: currentLocation ? currentLocation[1] : null,
        radiusMeters: Number(form.radiusMeters) || 100,
        locationName: form.locationName,
      },
    };

    console.log("🔹 Worker ID:", worker.id);
    console.log("🌍 [EDIT] Browser timezone detected:", Intl.DateTimeFormat().resolvedOptions().timeZone);
    console.log(
      "🔹 API URL:",
      `${BASE_URL}/company-admins/workers/${worker.id}/attendance-settings`
    );
    console.log("📤 [EDIT] Payload being sent (UTC schedule values):", payload);
 setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/company-admins/workers/${worker.id}/attendance-settings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(decryptedToken ? { Authorization: `Bearer ${decryptedToken}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );

      console.log("🔹 Response status:", res.status);

      const text = await res.text();
      console.log("🔹 Raw Response:", text);

      if (!res.ok) {
        toast.error(getApiErrorMessage(text));
        return;
      }

      const json = JSON.parse(text);
      console.log("✅ Parsed JSON:", json);
      const lang = i18n.language?.split("-")[0] || "en";
      const successMessage =
        (lang === "es" && json.message_es) ||
        json.message_en ||
        json.message ||
        t("editModal.toast.success");
      toast.success(successMessage);

      onUpdateSuccess();
      onClose();
    } catch (err) {
      console.error("❌ Update Error", err);
      toast.error(err.message || t("editModal.toast.error"));
    }
     finally {
      setLoading(false); // ✅ stop loader
    }
  };

  const getMarkerIcon = () => {
    if (!window.google?.maps) {
      return { url: markerIconUrl };
    }

    return {
      url: markerIconUrl,
      scaledSize: new window.google.maps.Size(25, 41),
      anchor: new window.google.maps.Point(12, 41),
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle
        sx={{
          ...textStyles.h1,
          fontWeight: 600,
          fontSize: "22px",
          color: "#003149",
        }}
      >
     {t("editModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        {/* Days row */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, gap: 1 }}>
          {fullDays.map((day) => {
            const short = day.slice(0, 3);
            const isSelected = form.workingDays.includes(short);

            return (
              <Box
                key={day}
                onClick={() => toggleDay(short)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  userSelect: "none",
                  bgcolor: isSelected ? "#1976d2" : "#e0e0e0",
                  color: isSelected ? "#fff" : "#000",
                  transition: "all 0.2s",
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Box>

        {/* Map */}
        {currentLocation ? (
          <Box sx={{ mb: 3, height: 250, borderRadius: "8px", overflow: "hidden" }}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={{ lat: currentLocation[0], lng: currentLocation[1] }}
              zoom={15}
              options={{
                fullscreenControl: false,
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              <Marker
                position={{ lat: currentLocation[0], lng: currentLocation[1] }}
                draggable
                icon={getMarkerIcon()}
                onClick={() => setIsMapPopupOpen(true)}
                onDragEnd={(e) => {
                  setCurrentLocation([e.latLng.lat(), e.latLng.lng()]);
                }}
              />
              {isMapPopupOpen && (
                <InfoWindow
                  position={{ lat: currentLocation[0], lng: currentLocation[1] }}
                  onCloseClick={() => setIsMapPopupOpen(false)}
                >
                  <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
                    <strong>{worker?.name || "Worker"}</strong>
                    <br />
                    {form.locationName || "Location"}
                    <br />
                    Radius: {form.radiusMeters}m
                  </Box>
                </InfoWindow>
              )}

              <Circle
                center={{ lat: currentLocation[0], lng: currentLocation[1] }}
                radius={Number(form.radiusMeters) || 100}
                options={{
                  strokeColor: "blue",
                  strokeOpacity: 1,
                  strokeWeight: 1,
                  fillColor: "blue",
                  fillOpacity: 0.1,
                }}
              />
            </GoogleMap>
          </Box>
        ) : (
          <Typography sx={{ mb: 3, textAlign: "center", color: "gray" }}>
         {t("editModal.map.fetching")}  
          </Typography>
        )}

        {/* Location Name */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5,color:"#44546F", fontFamily: "Poppins, sans-serif" }}>
          {t("editModal.labels.locationName")}
          </Typography>
          <TextField
            type="text"
            name="locationName"
             placeholder={t("editModal.labels.locationName")} 
            value={form.locationName}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Radius Input */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5,color:"#44546F", fontFamily: "Poppins, sans-serif" }}>
        {t("editModal.labels.locationRadius")}
          </Typography>
          <TextField
            type="number"
            name="radiusMeters"
            value={form.radiusMeters}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Shift Times */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, color:"#44546F",fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.shiftStart")}
            </Typography>
            <TextField
              type="time"
              name="startTime"
              value={form.startTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5,color:"#44546F", fontFamily: "Poppins, sans-serif" }}>
               {t("editModal.labels.shiftEnd")}
            </Typography>
            <TextField
              type="time"
              name="endTime"
              value={form.endTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Grace + Break */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", color:"#44546F",mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
                {t("editModal.labels.graceMinutes")}
            </Typography>
            <TextField
              type="number"
              name="graceMinutes"
              value={form.graceMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{ 
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px",color:"#44546F", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
           {t("editModal.labels.breakMinutes")}
            </Typography>
            <TextField
              type="number"
              name="breakMinutes"
              value={form.breakMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{ 
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            fontSize: 14,
            textTransform: "none",
            borderRadius: "5px",
            fontFamily: "Poppins, sans-serif",
            color: "#006EC2",
            border: "2px solid #006EC2",
            px: 2,
            py: 1,
            height: "38px",
          }}
        >
       {t("editModal.buttons.cancel")}
        </Button>
        {/* <CustomButton label="Update" size="small" onClick={handleSubmit} sx={{ px: 2 }} /> */}
        <CustomButton
  size="small"
  onClick={handleSubmit}
  disabled={loading} // disable while loading
  sx={{ px: 2, display: "flex", alignItems: "center" }}
  label={
    loading ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} sx={{ color: "white" }} />
   {t("editModal.buttons.update")}
      </Box>
    ) : (
     t("editModal.buttons.update")
    )
  }
/>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleModal;










